import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Box } from '../../../../../../../../../CommonComponents/OPL/Box/Box';
import { Label } from '../../../../../../../../../CommonComponents/OPL/Label/Label';
import { PopupSelect } from '../../../../../../../../../CommonComponents/OPL/Select/PopupSelect/PopupSelect';
import { FormValues, Purchase } from '../../AnnuityPurchase.meta';
import { usePurchaseAmountForm } from './usePurchaseAmountForm';
import { InputNumberMask } from '../../../../../../../../../CommonComponents/OPL/Input/InputNumberMask';
import { formatCurrency } from '../../../../../../../../../CommonUtils/formatNumber';
import { Checkbox } from '../../../../../../../../../CommonComponents/OPL/Checkbox/Checkbox';
import { HelperText } from '../../../../../../../../../CommonComponents/OPL/HelperText/HelperText';
import { Input } from '../../../../../../../../../CommonComponents/OPL/Input/Input';
import { OwnerModelDTO } from '../../../../../../../../../Profile/Accounts/api/useAccountsQuery';

interface Props {
  purchase: Purchase;
  index: number;
  fpId: string;
  isReadOnly: boolean;
  accountOwner: string | null;
  ownerModel: OwnerModelDTO[] | null;
  annuityIsQualified: boolean | null;
  annuityIsRoth: boolean | null;
}

export const PurchaseAmountForm = ({
  purchase,
  index,
  fpId,
  isReadOnly,
  accountOwner,
  ownerModel,
  annuityIsQualified,
  annuityIsRoth,
}: Props) => {
  const formBag = useFormContext<FormValues>();

  const { isLoading, data } = usePurchaseAmountForm({
    year: purchase.PurchaseYear,
    fpId,
    accountOwner,
    ownerModel,
    annuityIsQualified,
    annuityIsRoth,
    isApplied: purchase.IsApplied,
  });

  const { accountOptions, sourceAccounts } = data;

  const availableAmount = purchase.SourceAccounts.reduce((acc, { Id }) => {
    const foundAccount = sourceAccounts.find(account => account.Id === Id);

    if (foundAccount) {
      return acc + foundAccount.AvailableAmount;
    }

    return acc;
  }, 0);

  const purchaseAmountError = formBag.formState.errors.Purchases?.[index]?.PurchaseAmount?.message;

  return (
    <>
      <Controller
        control={formBag.control}
        name={`Purchases.${index}.SourceAccounts`}
        render={({ field: { ref, value, onChange } }) => {
          return (
            <Box>
              {purchase.IsApplied ? (
                <Input
                  value={`${purchase.SourceAccounts.length} account${
                    purchase.SourceAccounts.length > 1 ? 's' : ''
                  } selected`}
                  disabled
                  label="From"
                />
              ) : (
                <>
                  <Label mb="4">From</Label>

                  <PopupSelect
                    innerRef={ref}
                    isMulti
                    variant="checkbox"
                    value={accountOptions.filter(source =>
                      value.map(({ Id }) => Id).includes(source.value),
                    )}
                    options={accountOptions}
                    onChange={val =>
                      onChange(
                        val.map(({ value: Id, metaData }) => ({
                          Id,
                          ...metaData,
                        })),
                      )
                    }
                    isLoading={isLoading}
                    disabled={isReadOnly || !purchase.PurchaseYear}
                    label={
                      value && Array.isArray(value) && value.length
                        ? `${value.length} account${value.length > 1 ? 's' : ''} selected`
                        : 'Select account(s)'
                    }
                  />
                </>
              )}

              {formBag.formState.errors.Purchases?.[index]?.SourceAccounts && (
                <HelperText variant="error" mt="4">
                  {(formBag.formState.errors.Purchases?.[index]?.SourceAccounts as any).message}
                </HelperText>
              )}
            </Box>
          );
        }}
      />

      <Controller
        control={formBag.control}
        name={`Purchases.${index}.PurchaseAmount`}
        render={({ field: { value, onChange, ...field } }) => (
          <Box>
            <InputNumberMask
              {...field}
              label="Purchase Amount"
              value={value ?? ''}
              decimalScale={2}
              allowNegative={false}
              leftIcon="far fa-dollar-sign"
              onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
              disabled={
                isReadOnly || purchase.SourceAccounts.length === 0 || purchase.UseFullAccountValue
              }
              formVariant={purchaseAmountError ? 'error' : undefined}
              helperText={purchase.IsApplied ? undefined : purchaseAmountError}
            />

            {!purchase.IsApplied && purchase.SourceAccounts.length > 0 && (
              <HelperText mt="4">{`Available amount: ${formatCurrency(
                availableAmount,
              )}`}</HelperText>
            )}
          </Box>
        )}
      />

      <Checkbox
        {...formBag.register(`Purchases.${index}.UseFullAccountValue`, {
          onChange: evt => {
            if (evt.target.checked) {
              formBag.setValue(`Purchases.${index}.PurchaseAmount`, null);
            }
          },
        })}
        disabled={isReadOnly || purchase.SourceAccounts.length === 0}
      >
        {`Select Full Account${purchase.SourceAccounts.length > 1 ? 's' : ''} Value`}
      </Checkbox>
    </>
  );
};
